(() => {
  const DB_NAME = 'ops-artwork-scratchpad';
  const STORE = 'uploads';
  const MAX_RESULTS = 12;
  const SUPPORTED = /image\/(png|jpeg|webp|gif|svg\+xml)/i;

  const uid = () => `art-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 9)}`;
  const cleanText = (value = '') => String(value).replace(/\s+/g, ' ').trim();
  const filenameFromUrl = (url, fallback = 'brand-artwork') => {
    try {
      const raw = decodeURIComponent(new URL(url).pathname.split('/').pop() || fallback).split('?')[0];
      return raw && /\.[a-z0-9]{2,5}$/i.test(raw) ? raw : `${raw || fallback}.png`;
    } catch (_) { return `${fallback}.png`; }
  };
  const absoluteUrl = (value, base) => {
    try { return new URL(value, base).href; } catch (_) { return ''; }
  };
  const normaliseUrl = (value) => {
    let url = cleanText(value);
    if (!url) throw new Error('Enter your website or public business page first.');
    if (!/^https?:\/\//i.test(url)) url = `https://${url}`;
    const parsed = new URL(url);
    if (!/^https?:$/.test(parsed.protocol)) throw new Error('Use a normal http or https website address.');
    return parsed.href;
  };

  async function openDb() {
    return new Promise((resolve, reject) => {
      const req = indexedDB.open(DB_NAME, 1);
      req.onupgradeneeded = () => {
        if (!req.result.objectStoreNames.contains(STORE)) req.result.createObjectStore(STORE, { keyPath: 'id' });
      };
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => reject(req.error || new Error('My Artwork could not be opened.'));
    });
  }

  async function putRecord(record) {
    const db = await openDb();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(STORE, 'readwrite');
      tx.objectStore(STORE).put(record);
      tx.oncomplete = resolve;
      tx.onerror = () => reject(tx.error || new Error('Artwork could not be saved.'));
    });
  }

  async function blobToImage(blob) {
    const url = URL.createObjectURL(blob);
    try {
      const image = await new Promise((resolve, reject) => {
        const img = new Image();
        img.onload = () => resolve(img);
        img.onerror = () => reject(new Error('This image could not be opened.'));
        img.src = url;
      });
      return image;
    } finally {
      setTimeout(() => URL.revokeObjectURL(url), 0);
    }
  }

  async function analyse(blob) {
    const image = await blobToImage(blob);
    const widthPx = image.naturalWidth || image.width || 0;
    const heightPx = image.naturalHeight || image.height || 0;
    let hasTransparency = false;
    if (widthPx && heightPx && !/svg/i.test(blob.type)) {
      const canvas = document.createElement('canvas');
      const scale = Math.min(1, 600 / Math.max(widthPx, heightPx));
      canvas.width = Math.max(1, Math.round(widthPx * scale));
      canvas.height = Math.max(1, Math.round(heightPx * scale));
      const ctx = canvas.getContext('2d', { willReadFrequently: true });
      ctx.drawImage(image, 0, 0, canvas.width, canvas.height);
      try {
        const data = ctx.getImageData(0, 0, canvas.width, canvas.height).data;
        for (let i = 3; i < data.length; i += 4) { if (data[i] < 250) { hasTransparency = true; break; } }
      } catch (_) {}
    }
    return { image, widthPx, heightPx, hasTransparency };
  }

  async function thumbnail(blob, analysis) {
    const image = analysis?.image || await blobToImage(blob);
    const max = 520;
    const scale = Math.min(1, max / Math.max(image.naturalWidth || image.width, image.naturalHeight || image.height));
    const canvas = document.createElement('canvas');
    canvas.width = Math.max(1, Math.round((image.naturalWidth || image.width) * scale));
    canvas.height = Math.max(1, Math.round((image.naturalHeight || image.height) * scale));
    canvas.getContext('2d').drawImage(image, 0, 0, canvas.width, canvas.height);
    return new Promise((resolve) => canvas.toBlob(resolve, 'image/png', .9));
  }

  async function hashBlob(blob) {
    if (!crypto?.subtle) return '';
    try {
      const buffer = await blob.arrayBuffer();
      const digest = await crypto.subtle.digest('SHA-256', buffer);
      return [...new Uint8Array(digest)].map(v => v.toString(16).padStart(2, '0')).join('');
    } catch (_) { return ''; }
  }

  function intendedDimensions(width, height, defaultSize = 50) {
    if (!width || !height) return { width: defaultSize, height: defaultSize };
    return width >= height ? { width: defaultSize, height: defaultSize * height / width } : { width: defaultSize * width / height, height: defaultSize };
  }

  async function saveBlob(blob, name, sourceUrl = '', brandUrl = '') {
    if (!SUPPORTED.test(blob.type || '')) throw new Error(`${name}: unsupported image type.`);
    const file = blob instanceof File ? blob : new File([blob], name, { type: blob.type || 'image/png', lastModified: Date.now() });
    const info = await analyse(file);
    const intended = intendedDimensions(info.widthPx, info.heightPx, 50);
    const now = new Date().toISOString();
    const record = {
      id: uid(), kind: 'artwork', name: file.name, type: file.type, size: file.size, file,
      thumbnail: await thumbnail(file, info), widthPx: info.widthPx, heightPx: info.heightPx,
      hasTransparency: info.hasTransparency, intendedWidthMm: intended.width, intendedHeightMm: intended.height,
      notes: brandUrl ? `Brand artwork imported from ${brandUrl}` : 'Brand artwork added from the Branding page.',
      tags: ['brand-import'], hash: await hashBlob(file), sourceUrl, createdAt: now, updatedAt: now,
    };
    await putRecord(record);
    return record;
  }

  function collectAssetsFromHtml(html, pageUrl) {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const found = [];
    const push = (value, label = '', kind = 'image') => {
      const url = absoluteUrl(value, pageUrl);
      if (!url || !/^https?:/i.test(url) || found.some(item => item.url === url)) return;
      found.push({ url, label: cleanText(label) || filenameFromUrl(url, kind), kind });
    };
    doc.querySelectorAll('meta[property="og:image"],meta[name="twitter:image"],meta[property="twitter:image"]').forEach(el => push(el.content, 'Featured image', 'featured'));
    doc.querySelectorAll('link[rel~="icon"],link[rel="apple-touch-icon"]').forEach(el => push(el.href, 'Site icon', 'icon'));
    doc.querySelectorAll('img').forEach(img => {
      const value = img.currentSrc || img.getAttribute('src') || img.getAttribute('data-src') || img.getAttribute('data-lazy-src');
      if (!value || value.startsWith('data:')) return;
      push(value, img.alt || img.getAttribute('aria-label') || '', /logo|brand|mark/i.test(`${value} ${img.alt || ''}`) ? 'logo' : 'image');
    });
    const score = item => (/logo|brand|mark/i.test(`${item.url} ${item.label}`) ? 50 : 0) + (item.kind === 'featured' ? 30 : 0) + (item.kind === 'icon' ? 10 : 0);
    return found.sort((a,b) => score(b) - score(a)).slice(0, MAX_RESULTS);
  }

  class OpsBrandImporter extends HTMLElement {
    connectedCallback() {
      this.urlInput = this.querySelector('[data-brand-url]');
      this.findButton = this.querySelector('[data-brand-find]');
      this.status = this.querySelector('[data-brand-status]');
      this.resultsWrap = this.querySelector('[data-brand-results-wrap]');
      this.results = this.querySelector('[data-brand-results]');
      this.saveButton = this.querySelector('[data-brand-save-selected]');
      this.fileInput = this.querySelector('[data-brand-files]');
      this.endpoint = cleanText(this.dataset.importEndpoint || '');
      this.candidates = [];
      this.findButton?.addEventListener('click', () => this.find());
      this.urlInput?.addEventListener('keydown', e => { if (e.key === 'Enter') { e.preventDefault(); this.find(); } });
      this.saveButton?.addEventListener('click', () => this.saveSelected());
      this.fileInput?.addEventListener('change', () => this.saveFiles([...this.fileInput.files]));
      const previous = localStorage.getItem('opsBrandSourceUrl');
      if (previous && !this.urlInput.value) this.urlInput.value = previous;
    }

    setStatus(message, type = '') {
      if (!this.status) return;
      this.status.textContent = message || '';
      this.status.classList.toggle('is-error', type === 'error');
      this.status.classList.toggle('is-success', type === 'success');
    }

    async find() {
      let url;
      try { url = normaliseUrl(this.urlInput.value); } catch (error) { this.setStatus(error.message, 'error'); return; }
      this.urlInput.value = url;
      localStorage.setItem('opsBrandSourceUrl', url);
      this.findButton.disabled = true;
      this.setStatus('Looking for publicly available artwork and images…');
      try {
        let candidates = [];
        if (/\.(png|jpe?g|webp|gif|svg)(\?|#|$)/i.test(url)) {
          candidates = [{ url, label: filenameFromUrl(url), kind: 'direct image' }];
        } else if (this.endpoint) {
          const response = await fetch(this.endpoint, { method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify({ url }) });
          if (!response.ok) throw new Error(`Importer service returned ${response.status}.`);
          const data = await response.json();
          candidates = Array.isArray(data.assets) ? data.assets.filter(item => item?.url).slice(0, MAX_RESULTS) : [];
        } else {
          const response = await fetch(url, { mode: 'cors', credentials: 'omit', redirect: 'follow' });
          if (!response.ok) throw new Error(`Website returned ${response.status}.`);
          const html = await response.text();
          candidates = collectAssetsFromHtml(html, url);
        }
        if (!candidates.length) {
          this.render([]);
          this.setStatus('No usable public images were returned. Upload your logo or artwork below instead. Some websites and social platforms block automated access.');
          return;
        }
        this.render(candidates);
        this.setStatus(`Found ${candidates.length} possible asset${candidates.length === 1 ? '' : 's'}. Choose only files that belong to your business or that you have permission to print.`, 'success');
      } catch (error) {
        this.render([]);
        this.setStatus('This site is blocking direct browser access, so we could not inspect it automatically. Your web address has been kept as a reference. Upload your logo or artwork below and continue normally.', 'error');
        console.warn('OPS brand importer:', error);
      } finally { this.findButton.disabled = false; }
    }

    render(candidates) {
      this.candidates = candidates;
      this.results.replaceChildren();
      this.resultsWrap.hidden = !candidates.length;
      candidates.forEach((asset, index) => {
        const card = document.createElement('article');
        card.className = 'ops-brand-result';
        const label = cleanText(asset.label) || filenameFromUrl(asset.url);
        card.innerHTML = `<label><span class="ops-brand-result__image"><img loading="lazy" referrerpolicy="no-referrer" alt=""></span><span class="ops-brand-result__meta"><strong></strong><small></small></span><input type="checkbox" data-brand-result value="${index}"></label>`;
        card.querySelector('img').src = asset.url;
        card.querySelector('img').alt = label;
        card.querySelector('strong').textContent = label;
        card.querySelector('small').textContent = cleanText(asset.kind || 'public image');
        this.results.appendChild(card);
      });
    }

    async saveSelected() {
      const selected = [...this.querySelectorAll('[data-brand-result]:checked')].map(node => this.candidates[Number(node.value)]).filter(Boolean);
      if (!selected.length) { this.setStatus('Choose at least one image first.', 'error'); return; }
      this.saveButton.disabled = true;
      let saved = 0, blocked = 0;
      this.setStatus(`Adding ${selected.length} selected asset${selected.length === 1 ? '' : 's'} to My Artwork…`);
      for (const asset of selected) {
        try {
          const response = await fetch(asset.url, { mode: 'cors', credentials: 'omit' });
          if (!response.ok) throw new Error(`HTTP ${response.status}`);
          const blob = await response.blob();
          await saveBlob(blob, filenameFromUrl(asset.url), asset.url, this.urlInput.value);
          saved += 1;
        } catch (error) {
          blocked += 1;
          console.warn('OPS brand asset could not be saved:', asset.url, error);
        }
      }
      if (saved) window.dispatchEvent(new CustomEvent('ops:artwork-scratchpad-updated', { detail: { action: 'brand-import', count: saved } }));
      this.saveButton.disabled = false;
      if (saved && !blocked) this.setStatus(`${saved} asset${saved === 1 ? '' : 's'} added to My Artwork.`, 'success');
      else if (saved) this.setStatus(`${saved} saved. ${blocked} could be displayed but the source site blocked us from copying the file into My Artwork.`, 'error');
      else this.setStatus('The source site allowed the images to display but blocked browser download. Upload the original files below instead.', 'error');
    }

    async saveFiles(files) {
      const valid = files.filter(file => SUPPORTED.test(file.type || ''));
      if (!valid.length) { this.setStatus('Choose PNG, JPEG, WebP, GIF or SVG artwork.', 'error'); return; }
      this.setStatus(`Saving ${valid.length} file${valid.length === 1 ? '' : 's'} to My Artwork…`);
      let saved = 0;
      for (const file of valid) {
        try { await saveBlob(file, file.name, '', this.urlInput.value || ''); saved += 1; }
        catch (error) { console.warn(error); }
      }
      if (saved) {
        window.dispatchEvent(new CustomEvent('ops:artwork-scratchpad-updated', { detail: { action: 'brand-upload', count: saved } }));
        this.setStatus(`${saved} file${saved === 1 ? '' : 's'} added to My Artwork.`, 'success');
      } else this.setStatus('Those files could not be saved. Please try again.', 'error');
      this.fileInput.value = '';
    }
  }

  if (!customElements.get('ops-brand-importer')) customElements.define('ops-brand-importer', OpsBrandImporter);
})();
